import { Profile, ProfileData, DispatchAction } from '../types/Profile';
import { ACTIONS } from '../components/profile-form-components/ProfileFormReducer';
import { processProfileError, ProcessedError, logError } from './errorHandling';
import DOMPurify from "dompurify";

// Function to find the first tab with validation errors
const getFirstTabWithErrors = (validationErrors: any, nestedErrors?: any) => {
  const fieldToTab = {
    // General Info (Tab 0)
    'first_name': 0,
    'last_name': 0,
    'phone': 0,
    'zip': 0,
    'street_address': 0,
    'city': 0,
    'state': 0,
    
    // Experience (Tab 2)
    'experience_or_resume': 2,
    'experiences': 2,
    'educations': 2,
    
    // Availability (Tab 3)
    'hourly_rate': 3,
    'maximum_traveling_distance': 3,
    'positions': 3,
    'availability': 3,
    'position_type_ids': 3,
    'job_type_ids': 3,
  };

  // Check flat validation errors
  for (const field in validationErrors) {
    if (validationErrors[field]) {
      if (fieldToTab[field] !== undefined) {
        return fieldToTab[field];
      }
      // Check for nested field patterns
      if (field.startsWith('experiences') || field.startsWith('educations')) {
        return 2;
      }
    }
  }

  // Check nested validation errors
  if (nestedErrors) {
    if (nestedErrors.experiences?.some((exp: any) => exp && Object.values(exp).some(error => error))) {
      return 2;
    }
    if (nestedErrors.educations?.some((edu: any) => edu && Object.values(edu).some(error => error))) {
      return 2;
    }
  }

  return 0; // Default to first tab
};

// Enhanced error processing for Rails validation errors
const processValidationErrors = (railsErrors: any, experiences?: any[], educations?: any[]) => {
  const errors: { [key: string]: string | null } = {};
  const nestedErrors: { experiences?: any[], educations?: any[] } = {};
  
  // Handle enhanced format with metadata and backward compatibility format
  const errorData = railsErrors._metadata?.field_errors || railsErrors.field_errors || railsErrors.errors || railsErrors;
  
  // Process each field's errors
  Object.keys(errorData).forEach(field => {
    const fieldErrors = errorData[field];
    const errorMessage = Array.isArray(fieldErrors) && fieldErrors.length > 0 
      ? fieldErrors[0] 
      : typeof fieldErrors === 'string' 
        ? fieldErrors 
        : null;

    // Handle nested validation errors for experiences
    if (field.startsWith('experiences.') || field.startsWith('experiences[')) {
      // Parse index and field name from patterns like:
      // "experiences.start_date", "experiences[0].start_date", "experiences.0.start_date"
      const match = field.match(/experiences(?:\[(\d+)\])?\.?(\d+)?\.?(\w+)/);
      if (match) {
        const index = parseInt(match[1] || match[2] || '0');
        const fieldName = match[3];
        
        if (!nestedErrors.experiences) nestedErrors.experiences = [];
        if (!nestedErrors.experiences[index]) nestedErrors.experiences[index] = {};
        nestedErrors.experiences[index][fieldName] = errorMessage;
        
        // Also add to main errors for ErrorSummary display
        errors[field] = errorMessage;
      }
    }
    // Handle generic experience errors without index - be more selective
    else if (field.startsWith('experiences.') && !field.includes('[')) {
      const fieldName = field.replace('experiences.', '');
      
      if (!nestedErrors.experiences) nestedErrors.experiences = [];
      
      // Instead of applying to ALL experiences, find the LAST incomplete experience
      // This is typically where the user was trying to add new data
      let targetIndex = -1;
      
      console.log(`DEBUG: Processing experience validation for '${fieldName}' field`, {
        fieldName,
        errorMessage: errorMessage,
        experiencesLength: experiences?.length,
        experiences: experiences?.map((exp, i) => ({
          index: i,
          employer: exp?.employer,
          title: exp?.title,
          start_date: exp?.start_date,
          _destroy: exp?._destroy
        }))
      });
      
      if (experiences && experiences.length > 0) {
        // Find the last experience that has some data but is missing this specific field
        for (let i = experiences.length - 1; i >= 0; i--) {
          const experience = experiences[i];
          if (experience && !experience._destroy) {
            // Check if this experience has any data (indicating it's being used)
            const hasAnyData = experience.employer || experience.title || 
                             experience.start_date || experience.end_date || experience.description;
            
            // And check if it's missing this specific field
            const isMissingField = !experience[fieldName] || experience[fieldName].trim() === '';
            
            console.log(`DEBUG: Experience ${i} analysis:`, {
              index: i,
              hasAnyData,
              isMissingField,
              fieldValue: experience[fieldName],
              fieldValueTrimmed: experience[fieldName]?.trim?.(),
              employer: experience.employer,
              title: experience.title,
              start_date: experience.start_date
            });
            
            if (hasAnyData && isMissingField) {
              targetIndex = i;
              break; // Found the most recent incomplete experience
            }
          }
        }
      }
      
      console.log(`DEBUG: Target index determination complete:`, {
        fieldName,
        targetIndex,
        foundTarget: targetIndex >= 0,
        experiencesLength: experiences?.length
      });
      
      // If we found a target experience, apply the error only to that one
      if (targetIndex >= 0) {
        if (!nestedErrors.experiences[targetIndex]) nestedErrors.experiences[targetIndex] = {};
        nestedErrors.experiences[targetIndex][fieldName] = errorMessage;
        console.log(`DEBUG: Applied error to experience ${targetIndex}:`, errorMessage);
      }
      // Otherwise, if no experiences exist, apply to index 0 for new experience forms
      else if (!experiences || experiences.length === 0) {
        if (!nestedErrors.experiences[0]) nestedErrors.experiences[0] = {};
        nestedErrors.experiences[0][fieldName] = errorMessage;
        console.log(`DEBUG: Applied error to default experience 0:`, errorMessage);
      }
      
      // Don't add generic nested errors to main errors since we handle them in nested form
      // This prevents duplication in the error summary
    }
    // Handle nested validation errors for educations
    else if (field.startsWith('educations.') || field.startsWith('educations[')) {
      const match = field.match(/educations(?:\[(\d+)\])?\.?(\d+)?\.?(\w+)/);
      if (match) {
        const index = parseInt(match[1] || match[2] || '0');
        const fieldName = match[3];
        
        if (!nestedErrors.educations) nestedErrors.educations = [];
        if (!nestedErrors.educations[index]) nestedErrors.educations[index] = {};
        nestedErrors.educations[index][fieldName] = errorMessage;
        
        // Also add to main errors for ErrorSummary display
        errors[field] = errorMessage;
      }
    }
    // Handle generic education errors without index - be more selective
    else if (field.startsWith('educations.') && !field.includes('[')) {
      const fieldName = field.replace('educations.', '');
      
      if (!nestedErrors.educations) nestedErrors.educations = [];
      
      // Instead of applying to ALL educations, find the LAST incomplete education
      // This is typically where the user was trying to add new data
      let targetIndex = -1;
      
      if (educations && educations.length > 0) {
        // Find the last education that has some data but is missing this specific field
        for (let i = educations.length - 1; i >= 0; i--) {
          const education = educations[i];
          if (education && !education._destroy) {
            // Check if this education has any data (indicating it's being used)
            const hasAnyData = education.school_name || education.qualification || 
                             education.start_date || education.end_date || education.description;
            
            // And check if it's missing this specific field
            const isMissingField = !education[fieldName] || education[fieldName].trim() === '';
            
            if (hasAnyData && isMissingField) {
              targetIndex = i;
              break; // Found the most recent incomplete education
            }
          }
        }
      }
      
      // If we found a target education, apply the error only to that one
      if (targetIndex >= 0) {
        if (!nestedErrors.educations[targetIndex]) nestedErrors.educations[targetIndex] = {};
        nestedErrors.educations[targetIndex][fieldName] = errorMessage;
      }
      // Otherwise, if no educations exist, apply to index 0 for new education forms
      else if (!educations || educations.length === 0) {
        if (!nestedErrors.educations[0]) nestedErrors.educations[0] = {};
        nestedErrors.educations[0][fieldName] = errorMessage;
      }
      
      // Don't add generic nested errors to main errors since we handle them in nested form
      // This prevents duplication in the error summary
    }
    // Handle top-level profile fields
    else if (errorMessage) {
      errors[field] = errorMessage;
    }
  });
  
  // Map to our expected validation error structure
  const result = {
    first_name: errors.first_name || null,
    last_name: errors.last_name || null,
    phone: errors.phone || null,
    zip: errors.zip || null,
    street_address: errors.street_address || null,
    city: errors.city || null,
    state: errors.state || null,
    hourly_rate: errors.hourly_rate || null,
    maximum_traveling_distance: errors.maximum_traveling_distance || null,
    positions: errors.position_type_ids || null,
    availability: errors.job_type_ids || null,
    experience_or_resume: errors.experience_or_resume || null,
    // Include all nested errors in the main validation errors for ErrorSummary
    ...Object.keys(errors).reduce((acc, key) => {
      if (key.startsWith('experiences.') || key.startsWith('educations.')) {
        acc[key] = errors[key];
      }
      return acc;
    }, {} as { [key: string]: string | null })
  };

  // Add nested errors if they exist
  if (nestedErrors.experiences || nestedErrors.educations) {
    return {
      ...result,
      _nested: nestedErrors
    };
  }

  return result;
};

export const submitProfile = (
  profile: Profile,
  dispatch: React.Dispatch<DispatchAction>,
  authenticity_token: string,
) => {
  // Filter out incomplete experiences that shouldn't be submitted
  const filteredExperiences = profile.experiences.filter((exp) => {
    // Always include experiences marked for deletion
    if (exp._destroy) return true;
    
    // Check if experience has any substantial data
    const hasAnyData = exp.employer || exp.title || exp.start_date || exp.end_date || exp.description;
    
    // If no data at all, don't submit (it's just an empty form)
    if (!hasAnyData) return false;
    
    // If it has data, check if it has the required fields for server validation
    // Only submit if it's complete enough to pass server validation
    const hasRequiredFields = exp.employer && exp.title && exp.start_date;
    const isCompleteExperience = hasRequiredFields && 
      exp.employer.trim().length > 0 && 
      exp.title.trim().length > 0 && 
      exp.start_date.trim().length > 0;
    
    return isCompleteExperience;
  });

  // Filter out incomplete educations that shouldn't be submitted
  const filteredEducations = profile.educations.filter((edu) => {
    // Always include educations marked for deletion
    if (edu._destroy) return true;
    
    // Check if education has any substantial data
    const hasAnyData = edu.school_name || edu.qualification || edu.start_date || edu.end_date || edu.description;
    
    // If no data at all, don't submit (it's just an empty form)
    if (!hasAnyData) return false;
    
    // If it has data, check if it has the required fields for server validation
    // Only submit if it's complete enough to pass server validation
    const hasRequiredFields = edu.school_name && edu.qualification && edu.start_date;
    const isCompleteEducation = hasRequiredFields && 
      edu.school_name.trim().length > 0 && 
      edu.qualification.trim().length > 0 && 
      edu.start_date.trim().length > 0;
    
    return isCompleteEducation;
  });

  const profileData: ProfileData = {
    first_name: profile.first_name,
    last_name: profile.last_name,
    phone: profile.phone,
    phone_ext: profile.phone_ext,
    educations_attributes: filteredEducations.map((edu) => ({
      ...edu,
      _destroy: edu._destroy || false,
    })),
    experiences_attributes: filteredExperiences.map((exp) => ({
      ...exp,
      _destroy: exp._destroy || false,
    })),
    years_experience: profile.years_experience,
    background: profile.background,
    street_address: profile.street_address,
    street_address_2: profile.street_address_2,
    city: profile.city,
    state: profile.state,
    zip: profile.zip,
    position_type_ids: profile.position_type_ids,
    skill_ids: profile.skill_ids,
    job_type_ids: profile.job_type_ids,
    hourly_rate: profile.hourly_rate,
    maximum_traveling_distance: profile.maximum_traveling_distance,
  };

  console.log('DEBUG: Profile submission data:', {
    original_experiences: profile.experiences,
    filtered_experiences: filteredExperiences,
    experiences_attributes: profileData.experiences_attributes,
    original_educations: profile.educations,
    filtered_educations: filteredEducations,
    educations_attributes: profileData.educations_attributes
  });

  const url = `/profiles/${profile.id}.json`;
  const data = { profile: profileData };

  dispatch({ type: ACTIONS.FORM_SUBMIT_START });

  return fetch(url, {
    method: 'put',
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": authenticity_token,
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify(data),
  })
    .then(async (response) => {
      const responseData = await response.json();

      if (response.ok) {
        dispatch({ type: ACTIONS.FORM_SUBMIT_SUCCESS });
        return responseData;
      } else {
        // Enhanced error handling with new controller response format
        const processedError = processProfileError({
          response: { status: response.status, data: responseData }
        });

        // Log error for debugging and analytics
        logError(processedError, {
          profileId: profile.id,
          responseStatus: response.status,
          responseData
        });

        if (response.status === 422) {
          // Handle validation errors with enhanced error processing
          const validationErrors = processValidationErrors(
            responseData, 
            profile.experiences, 
            profile.educations
          );
          
          // Dispatch regular validation errors
          // Process validation errors - exclude _nested from main validation errors
          const mainValidationErrors = { ...validationErrors } as any;
          delete mainValidationErrors._nested;
          
          dispatch({
            type: ACTIONS.FORM_SUBMIT_VALIDATION_ERRORS,
            payload: mainValidationErrors 
          });          // Dispatch nested validation errors if they exist
          const nestedErrors = (validationErrors as any)._nested;
          if (nestedErrors && typeof nestedErrors === 'object') {
            // Ensure nested errors have the correct structure
            const safeNestedErrors = {
              experiences: Array.isArray(nestedErrors.experiences) ? nestedErrors.experiences : [],
              educations: Array.isArray(nestedErrors.educations) ? nestedErrors.educations : []
            };
            
            dispatch({
              type: ACTIONS.SET_NESTED_VALIDATION_ERRORS,
              payload: safeNestedErrors
            });
          }
          
          // Auto-navigate to the first tab with errors
          const firstErrorTab = getFirstTabWithErrors(validationErrors, nestedErrors);
          dispatch({ type: ACTIONS.SET_TAB_INDEX, payload: firstErrorTab });
          
          // Scroll to form
          setTimeout(() => {
            const element = document.getElementById("profileeditformcontainer");
            element?.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 100);
          
          throw new Error("Validation errors occurred");
        } else {
          // Handle other HTTP errors (500, etc.)
          dispatch({ 
            type: ACTIONS.FORM_SUBMIT_FAILURE,
            payload: {
              error: processedError,
              canRetry: 'isRetryable' in processedError ? processedError.isRetryable : true
            }
          });
          throw new Error(processedError.message);
        }
      }
    })
    .then((response) => {
      const api_url = response.api_url.replace(".json", "");
      const whitelist = [
        "directdental.com", 
        "test.directdental.com", 
        "veracode.directdental.com",
        "localhost"
      ];
      const url = new URL(api_url, window.location.origin);
      if (whitelist.some(domain => url.hostname === domain || url.hostname.endsWith("." + domain))) {
        window.location.href = DOMPurify.sanitize(url.href);
      } else {
        console.error("Untrusted URL:", url.hostname);
        throw new Error(`Invalid redirect URL: ${url.hostname}`);
      }
    })
    .catch((error) => {
      console.error("Error during profile submission:", error);
      
      // Enhanced error logging
      if (error.message !== "Validation errors occurred") {
        // Log unexpected errors to console with context
        logError({
          type: 'server',
          message: error.message,
          isRetryable: true
        }, {
          profileId: profile.id,
          originalError: error,
          context: 'profile_submission'
        });

        // Only dispatch FORM_SUBMIT_FAILURE if it's not a validation error
        // and we haven't already dispatched it above
        if (!error.message.includes("Invalid redirect URL") && 
            !error.message.includes("Network response was not ok")) {
          dispatch({ type: ACTIONS.FORM_SUBMIT_FAILURE });
        }
      }
    });
};

/**
 * Enhanced submission with retry capability
 */
export const submitProfileWithRetry = async (
  profile: Profile,
  dispatch: React.Dispatch<DispatchAction>,
  authenticity_token: string,
  maxRetries: number = 2
): Promise<void> => {
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      await submitProfile(profile, dispatch, authenticity_token);
      return; // Success, exit retry loop
    } catch (error) {
      lastError = error as Error;
      
      // Don't retry validation errors
      if (lastError.message === "Validation errors occurred") {
        throw lastError;
      }
      
      // Don't retry on the last attempt
      if (attempt < maxRetries) {
        // Wait before retrying (exponential backoff)
        const delay = Math.pow(2, attempt) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  // If we get here, all retries failed
  throw lastError;
};
